import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ClipboardCheck, RefreshCw, User, ChevronRight, ChevronDown } from 'lucide-react';
import { EHS_DAY_TITLES, EHS_QUESTIONS } from '../../constants/ehsQuestions';

const API = process.env.REACT_APP_API_BASE_URL;

// ─── NOVAA dark tokens (match CIDashboard) ───────────────────────
const T = {
  bg:      'var(--nd-bg)',
  surface: 'var(--nd-surface)',
  border:  'var(--nd-w06)',
  text:    'var(--nd-text)',
  muted:   'var(--nd-tx40)',
};

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700;9..40,800&family=DM+Mono:wght@400;500&display=swap');
  .ehs-wrap { font-family: 'DM Sans', sans-serif; color: var(--nd-text); }
  .ehs-input {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 9px; color: var(--nd-text); font-family: 'DM Sans', sans-serif;
    font-size: 13px; font-weight: 600; padding: 7px 12px; outline: none;
  }
  .ehs-input:focus { border-color: rgba(96,165,250,0.5); }
  .ehs-btn {
    background: var(--nd-w05); border: 1px solid var(--nd-w12);
    border-radius: 9px; color: var(--nd-tx70); font-weight: 700;
    font-size: 12px; padding: 8px 14px; cursor: pointer; display: inline-flex;
    align-items: center; gap: 7px; transition: all 0.15s;
  }
  .ehs-btn:hover { background: var(--nd-w10); color: var(--nd-text-strong); }
  .ehs-table { width: 100%; border-collapse: collapse; }
  .ehs-table th {
    text-align: left; font-size: 9px; font-weight: 700; letter-spacing: 0.15em;
    color: var(--nd-tx35); text-transform: uppercase; padding: 10px 14px;
    border-bottom: 1px solid var(--nd-w08);
  }
  .ehs-table td {
    font-size: 13px; color: var(--nd-text); padding: 12px 14px;
    border-bottom: 1px solid var(--nd-w04);
  }
  .ehs-row { cursor: pointer; }
  .ehs-row:hover td { background: var(--nd-w03); }
  .ehs-count {
    font-family: 'DM Mono', monospace; font-weight: 700; font-size: 13px;
    padding: 2px 9px; border-radius: 6px; display: inline-block; min-width: 34px; text-align: center;
  }
  .ehs-yes { color: #22D3A5; background: rgba(34,211,165,0.12); }
  .ehs-no  { color: #F87171; background: rgba(248,113,113,0.12); }
  .ehs-no-zero { color: var(--nd-tx30); background: var(--nd-w04); }
  .ehs-qrow {
    display: flex; align-items: center; justify-content: space-between; gap: 12px;
    padding: 9px 0; border-bottom: 1px solid var(--nd-w04);
  }
  .ehs-qrow:last-child { border-bottom: none; }
`;

const todayStr = () => new Date().toISOString().split('T')[0];
const monthStartStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const dayTitle = (idx) =>
  (idx >= 0 && idx < EHS_DAY_TITLES.length) ? EHS_DAY_TITLES[idx] : `Day ${Number(idx) + 1}`;

const isYes = (v) => String(v).toLowerCase() === 'yes';

const countAnswers = (answers) => {
  let yes = 0, no = 0;
  if (answers && typeof answers === 'object') {
    for (const v of Object.values(answers)) {
      const s = String(v).toLowerCase();
      if (s === 'yes') yes += 1;
      else if (s === 'no') no += 1;
    }
  }
  return { yes, no };
};

// Sort answer entries by question number (q7 before q10).
const sortedAnswers = (answers) =>
  Object.entries(answers || {}).sort(
    (a, b) => (parseInt(a[0].slice(1), 10) || 0) - (parseInt(b[0].slice(1), 10) || 0)
  );

const EHSReports = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [from, setFrom] = useState(monthStartStr());
  const [to, setTo] = useState(todayStr());
  const [expanded, setExpanded] = useState(() => new Set());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setExpanded(new Set());
      const params = new URLSearchParams();
      if (from) params.append('from', from);
      if (to) params.append('to', to);
      const res = await axios.get(`${API}/ehs/submissions?${params.toString()}`);
      setRows(res.data?.data || []);
    } catch (err) {
      console.error('EHS fetch error:', err);
      setError('Failed to load EHS submissions.');
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const toggleRow = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const totalNo = rows.reduce((sum, r) => sum + countAnswers(r.answers).no, 0);

  return (
    <div className="ehs-wrap" style={{ background: T.bg, padding: '14px 20px', minHeight: '100vh' }}>
      <style>{STYLES}</style>

      {/* Header */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, padding: '20px 24px', marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 42, height: 42, borderRadius: 11, background: 'rgba(34,211,165,0.12)', border: '1px solid rgba(34,211,165,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ClipboardCheck size={22} color="#22D3A5" />
            </div>
            <div>
              <div style={{ fontSize: 9, color: T.muted, letterSpacing: '0.18em', marginBottom: 4 }}>NOVAA · C&I SAFETY</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--nd-text-strong)', letterSpacing: '-0.02em', lineHeight: 1 }}>
                Daily EHS Checklists
              </div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>C&I field operatives' daily safety checklist submissions — click a row for the full answers</div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 9, color: T.muted, letterSpacing: '0.12em' }}>
              FROM
              <input type="date" className="ehs-input" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 9, color: T.muted, letterSpacing: '0.12em' }}>
              TO
              <input type="date" className="ehs-input" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} />
            </label>
            <button className="ehs-btn" onClick={fetchData} title="Refresh">
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${T.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', color: 'var(--nd-tx30)' }}>CHECKLIST SUBMISSIONS</div>
          <div style={{ fontSize: 11, color: T.muted, fontFamily: "'DM Mono', monospace" }}>
            {loading ? '…' : `${rows.length} submission${rows.length === 1 ? '' : 's'}${totalNo > 0 ? ` · ${totalNo} "No"` : ''}`}
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: T.muted, fontSize: 13 }}>Loading…</div>
        ) : error ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#F87171', fontSize: 13 }}>{error}</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: T.muted, fontSize: 13 }}>
            No EHS submissions in this date range.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="ehs-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>Person</th>
                  <th>Role</th>
                  <th>Checklist Day</th>
                  <th style={{ textAlign: 'center' }}>Yes</th>
                  <th style={{ textAlign: 'center' }}>No</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const { yes, no } = countAnswers(r.answers);
                  const open = expanded.has(r.id);
                  return (
                    <React.Fragment key={r.id}>
                      <tr className="ehs-row" onClick={() => toggleRow(r.id)}>
                        <td style={{ color: 'var(--nd-tx45)' }}>
                          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <User size={14} color="var(--nd-tx35)" />
                            <span style={{ fontWeight: 700 }}>{r.full_name || `User #${r.user_id}`}</span>
                          </div>
                        </td>
                        <td style={{ color: T.muted, fontFamily: "'DM Mono', monospace", fontSize: 12 }}>{r.role || 'N/A'}</td>
                        <td>{dayTitle(r.day_index)}</td>
                        <td style={{ textAlign: 'center' }}><span className="ehs-count ehs-yes">{yes}</span></td>
                        <td style={{ textAlign: 'center' }}><span className={`ehs-count ${no > 0 ? 'ehs-no' : 'ehs-no-zero'}`}>{no}</span></td>
                        <td style={{ fontFamily: "'DM Mono', monospace", color: 'var(--nd-tx70)' }}>{r.submission_date || '—'}</td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={7} style={{ padding: 0, background: 'var(--nd-w02)' }}>
                            <div style={{ padding: '10px 24px 14px 48px' }}>
                              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--nd-tx30)', marginBottom: 6 }}>
                                {dayTitle(r.day_index).toUpperCase()}
                              </div>
                              {sortedAnswers(r.answers).map(([qid, ans]) => (
                                <div key={qid} className="ehs-qrow">
                                  <span style={{ fontSize: 13, color: '#CBD5E1' }}>
                                    {EHS_QUESTIONS[qid] || qid}
                                  </span>
                                  <span className={`ehs-count ${isYes(ans) ? 'ehs-yes' : 'ehs-no'}`}>
                                    {isYes(ans) ? 'Yes' : 'No'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EHSReports;
